import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { RootState } from "@/app/rootReducer";
import { selectIntacctId } from "@/modules/hub/features/UserAppSettings/selectors/userAppSettingsSelectors";
import { useToast } from "@/components/ui/use-toast";
import {
  fetchROIDashboardDataAction,
  refreshROIDashboardDataAction,
} from "../slice/roiDashboardSlice";
import { AppDispatch } from "@/app/store";

export function useROIDashboard() {
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams();
  const activeCompanyIntacctId = useSelector(selectIntacctId);
  const { toast } = useToast();

  const intacctId =
    searchParams.get("intacctId") || activeCompanyIntacctId || "";

  const { dashboardData, loading, refreshing, error } = useSelector(
    (state: RootState) => state.roiDashboard,
  );

  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);

  const loadData = useCallback(() => {
    if (!intacctId) return;

    dispatch(
      fetchROIDashboardDataAction({
        intacctId,
      }),
    );
  }, [dispatch, intacctId]);

  const handleRefresh = useCallback(async () => {
    if (!intacctId || refreshing) return;

    try {
      await dispatch(refreshROIDashboardDataAction());

      toast({
        title: "Success",
        description:
          "Sales data refresh has been initiated. This may take a few moments.",
      });
      setTimeout(() => {
        loadData();
      }, 2000);
    } catch {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to refresh sales data",
      });
    }
  }, [dispatch, intacctId, refreshing, toast, loadData]);

  useEffect(() => {
    if (!hasInitiallyLoaded && intacctId) {
      loadData();
      setHasInitiallyLoaded(true);
    }
  }, [hasInitiallyLoaded, intacctId, loadData]);

  useEffect(() => {
    if (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error,
      });
    }
  }, [error, toast]);

  return {
    loading,
    refreshing,
    error,
    dashboardData,
    handleRefresh,
  };
}
